import type { ApiResponse } from '@weather-story/shared-types';
import {
  VALIDATED_SCHEMAS,
  type ValidatedEndpoint,
  validateResponse,
} from '../schemas/api-responses';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeader(): Record<string, string> {
    // Get token from localStorage (zustand persist)
    const stored = localStorage.getItem('weather-story-auth');
    if (stored) {
      try {
        const { state } = JSON.parse(stored);
        if (state?.accessToken) {
          return { Authorization: `Bearer ${state.accessToken}` };
        }
      } catch {
        /* corrupted localStorage */
      }
    }
    return {};
  }

  /**
   * Attempt to refresh the access token using the stored refresh token.
   * Updates localStorage directly to avoid circular dependency with authStore.
   */
  private async attemptTokenRefresh(): Promise<boolean> {
    const stored = localStorage.getItem('weather-story-auth');
    if (!stored) return false;

    let parsed: { state: Record<string, unknown>; version?: number };
    try {
      parsed = JSON.parse(stored);
    } catch {
      return false;
    }

    const refreshToken = parsed.state?.refreshToken;
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      const tokens = data.data?.tokens;
      if (!tokens?.accessToken) return false;

      // Update tokens in localStorage (zustand persist format)
      parsed.state.accessToken = tokens.accessToken;
      parsed.state.refreshToken = tokens.refreshToken;
      localStorage.setItem('weather-story-auth', JSON.stringify(parsed));
      return true;
    } catch {
      return false;
    }
  }

  private clearAuthState(): void {
    const stored = localStorage.getItem('weather-story-auth');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        parsed.state = {
          ...parsed.state,
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          onboardingCompleted: false,
        };
        localStorage.setItem('weather-story-auth', JSON.stringify(parsed));
      } catch {
        localStorage.removeItem('weather-story-auth');
      }
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getAuthHeader(),
      ...(options.headers as Record<string, string>),
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Auto-refresh on 401 (skip for auth endpoints and retries)
    if (response.status === 401 && !isRetry && !endpoint.startsWith('/auth/')) {
      // Coalesce concurrent refresh attempts into a single request
      if (!this.refreshPromise) {
        this.refreshPromise = this.attemptTokenRefresh().finally(() => {
          this.refreshPromise = null;
        });
      }

      const refreshed = await this.refreshPromise;
      if (refreshed) {
        return this.request<T>(endpoint, options, true);
      }

      // Refresh failed — clear auth state
      this.clearAuthState();
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Request failed');
    }

    return data;
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return this.request<T>(endpoint + queryString);
  }

  /** GET an endpoint that returns raw non-JSON content (e.g. HTML previews). */
  async getText(endpoint: string, params?: Record<string, string>): Promise<string> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${this.baseUrl}${endpoint}${queryString}`, {
      headers: this.getAuthHeader(),
    });
    if (!response.ok) {
      let message = 'Request failed';
      try {
        const data = await response.json();
        message = data.error?.message || data.error || message;
      } catch {
        // non-JSON error body
      }
      throw new Error(message);
    }
    return response.text();
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('image', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getAuthHeader(),
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Upload failed');
    }

    return data;
  }
}

export const api = new ApiClient(API_BASE_URL);

/**
 * Validates API response data if the endpoint has a registered schema
 */
function validateIfNeeded<T>(endpoint: string, data: unknown): T {
  // Extract base endpoint (remove query params and normalize)
  const baseEndpoint = endpoint.split('?')[0];

  // Check if this endpoint has a validation schema
  if (baseEndpoint in VALIDATED_SCHEMAS) {
    const schema = VALIDATED_SCHEMAS[baseEndpoint as ValidatedEndpoint];
    // Use type assertion since we know the schema matches the endpoint's expected type
    return validateResponse(data, schema as Parameters<typeof validateResponse>[1], endpoint) as T;
  }

  // No validation schema registered - return as-is
  return data as T;
}

// Adapter for shared-hooks ApiClient interface (catches throws, returns { error })
// IMPORTANT: Must return full response object { data: response }, NOT { data: response.data }
// Many API endpoints return { success, data, total } structure that hooks depend on
export const apiClientAdapter: import('@weather-story/shared-types').ApiClient = {
  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>) {
    try {
      const stringParams = params
        ? Object.fromEntries(
            Object.entries(params)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => [k, String(v)])
          )
        : undefined;
      const response = await api.get<T>(endpoint, stringParams);
      // Validate the unwrapped data if schema exists
      if (response.data) {
        validateIfNeeded(endpoint, response.data);
      }
      // Return full response as required by hooks
      return { data: response as T };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Request failed' };
    }
  },
  async post<T>(endpoint: string, body?: unknown) {
    try {
      const response = await api.post<T>(endpoint, body);
      return { data: response as T };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Request failed' };
    }
  },
  async put<T>(endpoint: string, body?: unknown) {
    try {
      const response = await api.patch<T>(endpoint, body);
      return { data: response as T };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Request failed' };
    }
  },
  async delete<T>(endpoint: string) {
    try {
      const response = await api.delete<T>(endpoint);
      return { data: response as T };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Request failed' };
    }
  },
};
